/**
 * <ry-color-picker>
 *
 * Color picker component with HSV grid, hue slider, and optional alpha slider.
 * Shows as a dropdown trigger by default; add `inline` for always-visible picker.
 *
 * Usage:
 * <ry-color-picker value="#ff0000"></ry-color-picker>
 * <ry-color-picker value="#ff0000" inline></ry-color-picker>
 * <ry-color-picker value="hsl(200, 100%, 50%)" format="hsl" opacity></ry-color-picker>
 */

import { RyElement } from '../core/ry-element.js';

// ─────────────────────────────────────────────────────────────────────────────
// Color conversion utilities (no external dependencies)
// ─────────────────────────────────────────────────────────────────────────────

export interface HSV {
  h: number; // 0-360
  s: number; // 0-100
  v: number; // 0-100
}

export interface RGB {
  r: number; // 0-255
  g: number; // 0-255
  b: number; // 0-255
}

export interface HSL {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

function hsvToRgb(hsv: HSV): RGB {
  const h = hsv.h / 360;
  const s = hsv.s / 100;
  const v = hsv.v / 100;

  let r = 0, g = 0, b = 0;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

function rgbToHsv(rgb: RGB): HSV {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    v: Math.round(v * 100)
  };
}

function hsvToHsl(hsv: HSV): HSL {
  const s = hsv.s / 100;
  const v = hsv.v / 100;

  const l = v * (1 - s / 2);
  const sl = l === 0 || l === 1 ? 0 : (v - l) / Math.min(l, 1 - l);

  return {
    h: hsv.h,
    s: Math.round(sl * 100),
    l: Math.round(l * 100)
  };
}

function hslToHsv(hsl: HSL): HSV {
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  const v = l + s * Math.min(l, 1 - l);
  const sv = v === 0 ? 0 : 2 * (1 - l / v);

  return {
    h: hsl.h,
    s: Math.round(sv * 100),
    v: Math.round(v * 100)
  };
}

function rgbToHex(rgb: RGB): string {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

function hexToRgb(hex: string): RGB | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    // Try 3-character hex
    const short = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
    if (short) {
      return {
        r: parseInt(short[1]! + short[1]!, 16),
        g: parseInt(short[2]! + short[2]!, 16),
        b: parseInt(short[3]! + short[3]!, 16)
      };
    }
    return null;
  }
  return {
    r: parseInt(result[1]!, 16),
    g: parseInt(result[2]!, 16),
    b: parseInt(result[3]!, 16)
  };
}

function parseColor(input: string): { hsv: HSV; alpha: number } | null {
  const str = input.trim().toLowerCase();

  // Hex format
  if (str.startsWith('#')) {
    // Check for 8-character hex (with alpha)
    const hexAlpha = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(str);
    if (hexAlpha) {
      const rgb = {
        r: parseInt(hexAlpha[1]!, 16),
        g: parseInt(hexAlpha[2]!, 16),
        b: parseInt(hexAlpha[3]!, 16)
      };
      const alpha = parseInt(hexAlpha[4]!, 16) / 255;
      return { hsv: rgbToHsv(rgb), alpha: Math.round(alpha * 100) };
    }
    const rgb = hexToRgb(str);
    if (rgb) return { hsv: rgbToHsv(rgb), alpha: 100 };
    return null;
  }

  // RGB/RGBA format
  const rgbMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/.exec(str);
  if (rgbMatch) {
    const rgb = {
      r: parseInt(rgbMatch[1]!, 10),
      g: parseInt(rgbMatch[2]!, 10),
      b: parseInt(rgbMatch[3]!, 10)
    };
    const alpha = rgbMatch[4] ? parseFloat(rgbMatch[4]) * 100 : 100;
    return { hsv: rgbToHsv(rgb), alpha };
  }

  // HSL/HSLA format
  const hslMatch = /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*([\d.]+))?\s*\)$/.exec(str);
  if (hslMatch) {
    const hsl = {
      h: parseFloat(hslMatch[1]!),
      s: parseFloat(hslMatch[2]!),
      l: parseFloat(hslMatch[3]!)
    };
    const alpha = hslMatch[4] ? parseFloat(hslMatch[4]) * 100 : 100;
    return { hsv: hslToHsv(hsl), alpha };
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export type ColorFormat = 'hex' | 'rgb' | 'hsl';

export class RyColorPicker extends RyElement {
  #hue = 0;
  #saturation = 100;
  #brightness = 100;
  #alpha = 100;

  #draggingGrid = false;
  #draggingHue = false;
  #draggingAlpha = false;

  #trigger: HTMLElement | null = null;
  #panel: HTMLElement | null = null;
  #grid: HTMLElement | null = null;
  #gridHandle: HTMLElement | null = null;
  #hueSlider: HTMLElement | null = null;
  #hueHandle: HTMLElement | null = null;
  #alphaSlider: HTMLElement | null = null;
  #alphaHandle: HTMLElement | null = null;
  #preview: HTMLElement | null = null;
  #input: HTMLInputElement | null = null;

  static observedAttributes = ['value', 'format', 'opacity', 'disabled', 'swatches', 'inline'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#bindEvents();
    this.#parseInitialValue();
    this.#updateAll();
  }

  #buildMarkup(): void {
    const isInline = this.hasAttribute('inline');
    const showAlpha = this.hasAttribute('opacity');
    const swatches = this.getAttribute('swatches');

    // Build the picker panel
    let panelHtml = `
      <div data-ry-target="grid" class="ry-color-picker__grid">
        <div data-ry-target="grid-handle" class="ry-color-picker__grid-handle" tabindex="0"></div>
      </div>
      <div data-ry-target="controls" class="ry-color-picker__controls">
        <div data-ry-target="sliders" class="ry-color-picker__sliders">
          <div data-ry-target="hue-slider" class="ry-color-picker__slider ry-color-picker__hue">
            <div data-ry-target="hue-handle" class="ry-color-picker__slider-handle" tabindex="0"></div>
          </div>
          ${showAlpha ? `
          <div data-ry-target="alpha-slider" class="ry-color-picker__slider ry-color-picker__alpha">
            <div data-ry-target="alpha-handle" class="ry-color-picker__slider-handle" tabindex="0"></div>
          </div>
          ` : ''}
        </div>
        <div data-ry-target="preview" class="ry-color-picker__preview">
          <span data-ry-target="preview-color" class="ry-color-picker__preview-color"></span>
        </div>
      </div>
      <div data-ry-target="input-row" class="ry-color-picker__input-row">
        <input data-ry-target="input" type="text" class="ry-color-picker__input" spellcheck="false" />
        <button data-ry-target="format-toggle" type="button" class="ry-color-picker__format-toggle">${this.format.toUpperCase()}</button>
      </div>
    `;

    // Add swatches if provided
    if (swatches) {
      const colors = swatches.split(';').map(c => c.trim()).filter(Boolean);
      if (colors.length > 0) {
        panelHtml += `<div data-ry-target="swatches" class="ry-color-picker__swatches">`;
        for (const color of colors) {
          panelHtml += `<button type="button" class="ry-color-picker__swatch" data-color="${color}" style="background-color: ${color}"></button>`;
        }
        panelHtml += `</div>`;
      }
    }

    if (isInline) {
      // Inline mode: just show the panel
      this.innerHTML = `<div data-ry-target="panel" class="ry-color-picker__panel ry-color-picker__panel--inline">${panelHtml}</div>`;
    } else {
      // Dropdown mode: trigger + panel
      this.innerHTML = `
        <button data-ry-target="trigger" type="button" class="ry-color-picker__trigger">
          <span data-ry-target="trigger-color" class="ry-color-picker__trigger-color"></span>
        </button>
        <div data-ry-target="panel" class="ry-color-picker__panel">${panelHtml}</div>
      `;
    }

    // Cache references
    this.#trigger = this.$('[data-ry-target="trigger"]');
    this.#panel = this.$('[data-ry-target="panel"]');
    this.#grid = this.$('[data-ry-target="grid"]');
    this.#gridHandle = this.$('[data-ry-target="grid-handle"]');
    this.#hueSlider = this.$('[data-ry-target="hue-slider"]');
    this.#hueHandle = this.$('[data-ry-target="hue-handle"]');
    this.#alphaSlider = this.$('[data-ry-target="alpha-slider"]');
    this.#alphaHandle = this.$('[data-ry-target="alpha-handle"]');
    this.#preview = this.$('[data-ry-target="preview"]');
    this.#input = this.$('[data-ry-target="input"]');
  }

  #bindEvents(): void {
    // Trigger click (dropdown mode)
    if (this.#trigger) {
      this.on(this.#trigger, 'click', this.#handleTriggerClick);
    }

    // Click outside to close
    this.on(document, 'click', this.#handleDocumentClick);

    // Escape to close
    this.on(document, 'keydown', this.#handleDocumentKeydown);

    // Grid dragging
    if (this.#grid) {
      this.on(this.#grid, 'pointerdown', this.#handleGridDown);
    }
    if (this.#gridHandle) {
      this.on(this.#gridHandle, 'keydown', this.#handleGridKeydown);
    }

    // Hue slider
    if (this.#hueSlider) {
      this.on(this.#hueSlider, 'pointerdown', this.#handleHueDown);
    }
    if (this.#hueHandle) {
      this.on(this.#hueHandle, 'keydown', this.#handleHueKeydown);
    }

    // Alpha slider
    if (this.#alphaSlider) {
      this.on(this.#alphaSlider, 'pointerdown', this.#handleAlphaDown);
    }
    if (this.#alphaHandle) {
      this.on(this.#alphaHandle, 'keydown', this.#handleAlphaKeydown);
    }

    // Document events for drag
    this.on(document, 'pointermove', this.#handlePointerMove);
    this.on(document, 'pointerup', this.#handlePointerUp);

    // Input
    if (this.#input) {
      this.on(this.#input, 'change', this.#handleInputChange);
      this.on(this.#input, 'keydown', this.#handleInputKeydown);
    }

    // Format toggle
    const formatToggle = this.$<HTMLButtonElement>('[data-ry-target="format-toggle"]');
    if (formatToggle) {
      this.on(formatToggle, 'click', this.#handleFormatToggle);
    }

    // Swatches
    const swatchContainer = this.$('[data-ry-target="swatches"]');
    if (swatchContainer) {
      this.on(swatchContainer, 'click', this.#handleSwatchClick);
    }
  }

  #parseInitialValue(): void {
    const value = this.getAttribute('value');
    if (value) {
      const parsed = parseColor(value);
      if (parsed) {
        this.#hue = parsed.hsv.h;
        this.#saturation = parsed.hsv.s;
        this.#brightness = parsed.hsv.v;
        this.#alpha = parsed.alpha;
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Dropdown handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleTriggerClick = (): void => {
    if (this.disabled) return;
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  };

  #handleDocumentClick = (e: Event): void => {
    if (this.state !== 'open') return;
    if (!this.contains(e.target as Node)) {
      this.close();
    }
  };

  #handleDocumentKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.state === 'open') {
      this.close();
      this.#trigger?.focus();
    }
  };

  open(): void {
    if (this.hasAttribute('inline')) return;
    // Position BEFORE showing to avoid flicker
    this.#positionPanel();
    this.state = 'open';
  }

  close(): void {
    if (this.hasAttribute('inline')) return;
    this.state = 'closed';
    this.removeAttribute('data-ry-position');
  }

  #positionPanel(): void {
    if (!this.#panel || !this.#trigger) return;

    const triggerRect = this.#trigger.getBoundingClientRect();
    const panelHeight = 320; // Approximate panel height
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    // Open upward if not enough space below and more space above
    if (spaceBelow < panelHeight && spaceAbove > spaceBelow) {
      this.setAttribute('data-ry-position', 'top');
    } else {
      this.setAttribute('data-ry-position', 'bottom');
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Grid handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleGridDown = (e: PointerEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#draggingGrid = true;
    this.#gridHandle?.focus();
    this.#updateFromGridPointer(e);
  };

  #updateFromGridPointer(e: PointerEvent): void {
    if (!this.#grid) return;
    const rect = this.#grid.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

    this.#saturation = Math.round(x * 100);
    this.#brightness = Math.round((1 - y) * 100);
    this.#updateAll();
    this.#emitInput();
  }

  #handleGridKeydown = (e: KeyboardEvent): void => {
    if (this.disabled) return;
    const step = e.shiftKey ? 10 : 1;
    let changed = false;

    switch (e.key) {
      case 'ArrowRight':
        this.#saturation = Math.min(100, this.#saturation + step);
        changed = true;
        break;
      case 'ArrowLeft':
        this.#saturation = Math.max(0, this.#saturation - step);
        changed = true;
        break;
      case 'ArrowUp':
        this.#brightness = Math.min(100, this.#brightness + step);
        changed = true;
        break;
      case 'ArrowDown':
        this.#brightness = Math.max(0, this.#brightness - step);
        changed = true;
        break;
    }

    if (changed) {
      e.preventDefault();
      this.#updateAll();
      this.#emitInput();
      this.#emitChange();
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Hue handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleHueDown = (e: PointerEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#draggingHue = true;
    this.#hueHandle?.focus();
    this.#updateFromHuePointer(e);
  };

  #updateFromHuePointer(e: PointerEvent): void {
    if (!this.#hueSlider) return;
    const rect = this.#hueSlider.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    this.#hue = Math.round(x * 360);
    this.#updateAll();
    this.#emitInput();
  }

  #handleHueKeydown = (e: KeyboardEvent): void => {
    if (this.disabled) return;
    const step = e.shiftKey ? 10 : 1;
    let changed = false;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        this.#hue = (this.#hue + step) % 360;
        changed = true;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        this.#hue = (this.#hue - step + 360) % 360;
        changed = true;
        break;
    }

    if (changed) {
      e.preventDefault();
      this.#updateAll();
      this.#emitInput();
      this.#emitChange();
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Alpha handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleAlphaDown = (e: PointerEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#draggingAlpha = true;
    this.#alphaHandle?.focus();
    this.#updateFromAlphaPointer(e);
  };

  #updateFromAlphaPointer(e: PointerEvent): void {
    if (!this.#alphaSlider) return;
    const rect = this.#alphaSlider.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    this.#alpha = Math.round(x * 100);
    this.#updateAll();
    this.#emitInput();
  }

  #handleAlphaKeydown = (e: KeyboardEvent): void => {
    if (this.disabled) return;
    const step = e.shiftKey ? 10 : 1;
    let changed = false;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        this.#alpha = Math.min(100, this.#alpha + step);
        changed = true;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        this.#alpha = Math.max(0, this.#alpha - step);
        changed = true;
        break;
    }

    if (changed) {
      e.preventDefault();
      this.#updateAll();
      this.#emitInput();
      this.#emitChange();
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Pointer move/up
  // ───────────────────────────────────────────────────────────────────────────

  #handlePointerMove = (e: PointerEvent): void => {
    if (this.#draggingGrid) {
      this.#updateFromGridPointer(e);
    } else if (this.#draggingHue) {
      this.#updateFromHuePointer(e);
    } else if (this.#draggingAlpha) {
      this.#updateFromAlphaPointer(e);
    }
  };

  #handlePointerUp = (): void => {
    if (this.#draggingGrid || this.#draggingHue || this.#draggingAlpha) {
      this.#draggingGrid = false;
      this.#draggingHue = false;
      this.#draggingAlpha = false;
      this.#emitChange();
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Input handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleInputChange = (): void => {
    if (!this.#input) return;
    const parsed = parseColor(this.#input.value);
    if (parsed) {
      this.#hue = parsed.hsv.h;
      this.#saturation = parsed.hsv.s;
      this.#brightness = parsed.hsv.v;
      if (this.hasAttribute('opacity')) {
        this.#alpha = parsed.alpha;
      }
      this.#updateAll();
      this.#emitChange();
    } else {
      // Reset to current value on invalid input
      this.#updateInput();
    }
  };

  #handleInputKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.#handleInputChange();
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Format toggle
  // ───────────────────────────────────────────────────────────────────────────

  #handleFormatToggle = (): void => {
    const formats: ColorFormat[] = ['hex', 'rgb', 'hsl'];
    const current = this.format;
    const idx = formats.indexOf(current);
    const next = formats[(idx + 1) % formats.length]!;
    this.format = next;

    const btn = this.$<HTMLButtonElement>('[data-ry-target="format-toggle"]');
    if (btn) btn.textContent = next.toUpperCase();

    this.#updateInput();
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Swatches
  // ───────────────────────────────────────────────────────────────────────────

  #handleSwatchClick = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('ry-color-picker__swatch')) {
      const color = target.dataset.color;
      if (color) {
        const parsed = parseColor(color);
        if (parsed) {
          this.#hue = parsed.hsv.h;
          this.#saturation = parsed.hsv.s;
          this.#brightness = parsed.hsv.v;
          if (this.hasAttribute('opacity')) {
            this.#alpha = parsed.alpha;
          }
          this.#updateAll();
          this.#emitInput();
          this.#emitChange();
        }
      }
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Update methods
  // ───────────────────────────────────────────────────────────────────────────

  #updateAll(): void {
    this.#updateTrigger();
    this.#updateGrid();
    this.#updateHueSlider();
    this.#updateAlphaSlider();
    this.#updatePreview();
    this.#updateInput();
    this.#syncValueAttribute();
  }

  #updateTrigger(): void {
    const triggerColor = this.$('[data-ry-target="trigger-color"]');
    if (triggerColor) {
      const rgb = hsvToRgb({ h: this.#hue, s: this.#saturation, v: this.#brightness });
      const alpha = this.hasAttribute('opacity') ? this.#alpha / 100 : 1;
      (triggerColor as HTMLElement).style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }
  }

  #updateGrid(): void {
    if (this.#grid) {
      // Set grid background to current hue at full saturation
      const rgb = hsvToRgb({ h: this.#hue, s: 100, v: 100 });
      this.#grid.style.setProperty('--grid-hue-color', `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
    }
    if (this.#gridHandle) {
      this.#gridHandle.style.left = `${this.#saturation}%`;
      this.#gridHandle.style.top = `${100 - this.#brightness}%`;
    }
  }

  #updateHueSlider(): void {
    if (this.#hueHandle) {
      this.#hueHandle.style.left = `${(this.#hue / 360) * 100}%`;
    }
  }

  #updateAlphaSlider(): void {
    if (this.#alphaSlider) {
      const rgb = hsvToRgb({ h: this.#hue, s: this.#saturation, v: this.#brightness });
      this.#alphaSlider.style.setProperty('--alpha-color', `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
    }
    if (this.#alphaHandle) {
      this.#alphaHandle.style.left = `${this.#alpha}%`;
    }
  }

  #updatePreview(): void {
    const previewColor = this.$('[data-ry-target="preview-color"]');
    if (previewColor) {
      const rgb = hsvToRgb({ h: this.#hue, s: this.#saturation, v: this.#brightness });
      const alpha = this.hasAttribute('opacity') ? this.#alpha / 100 : 1;
      (previewColor as HTMLElement).style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }
  }

  #updateInput(): void {
    if (this.#input) {
      this.#input.value = this.#formatValue();
    }
  }

  #syncValueAttribute(): void {
    // Update the value attribute without triggering attributeChangedCallback loop
    const formatted = this.#formatValue();
    if (this.getAttribute('value') !== formatted) {
      this.setAttribute('value', formatted);
    }
  }

  #formatValue(): string {
    const format = this.format;
    const rgb = hsvToRgb({ h: this.#hue, s: this.#saturation, v: this.#brightness });
    const showAlpha = this.hasAttribute('opacity');

    switch (format) {
      case 'hex': {
        const hex = rgbToHex(rgb);
        if (showAlpha && this.#alpha < 100) {
          const alphaHex = Math.round((this.#alpha / 100) * 255).toString(16).padStart(2, '0');
          return hex + alphaHex;
        }
        return hex;
      }
      case 'rgb': {
        if (showAlpha) {
          return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${(this.#alpha / 100).toFixed(2)})`;
        }
        return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
      }
      case 'hsl': {
        const hsl = hsvToHsl({ h: this.#hue, s: this.#saturation, v: this.#brightness });
        if (showAlpha) {
          return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${(this.#alpha / 100).toFixed(2)})`;
        }
        return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
      }
      default:
        return rgbToHex(rgb);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Events
  // ───────────────────────────────────────────────────────────────────────────

  #emitInput(): void {
    this.emit('input', { value: this.value, hsv: this.hsv, rgb: this.rgb });
  }

  #emitChange(): void {
    this.emit('change', { value: this.value, hsv: this.hsv, rgb: this.rgb });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Attribute changed
  // ───────────────────────────────────────────────────────────────────────────

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if (name === 'value' && this.#grid) {
      // External value change - parse and update
      if (newValue) {
        const parsed = parseColor(newValue);
        if (parsed) {
          this.#hue = parsed.hsv.h;
          this.#saturation = parsed.hsv.s;
          this.#brightness = parsed.hsv.v;
          if (this.hasAttribute('opacity')) {
            this.#alpha = parsed.alpha;
          }
          this.#updateTrigger();
          this.#updateGrid();
          this.#updateHueSlider();
          this.#updateAlphaSlider();
          this.#updatePreview();
          // Don't update input here to avoid cursor issues during typing
        }
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Public API
  // ───────────────────────────────────────────────────────────────────────────

  get value(): string {
    return this.#formatValue();
  }

  set value(val: string) {
    const parsed = parseColor(val);
    if (parsed) {
      this.#hue = parsed.hsv.h;
      this.#saturation = parsed.hsv.s;
      this.#brightness = parsed.hsv.v;
      if (this.hasAttribute('opacity')) {
        this.#alpha = parsed.alpha;
      }
      this.#updateAll();
    }
  }

  get format(): ColorFormat {
    return (this.getAttribute('format') as ColorFormat) || 'hex';
  }

  set format(val: ColorFormat) {
    this.setAttribute('format', val);
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  get hsv(): HSV {
    return { h: this.#hue, s: this.#saturation, v: this.#brightness };
  }

  get rgb(): RGB {
    return hsvToRgb(this.hsv);
  }

  get hsl(): HSL {
    return hsvToHsl(this.hsv);
  }

  get alpha(): number {
    return this.#alpha;
  }

  set alpha(val: number) {
    this.#alpha = Math.max(0, Math.min(100, val));
    this.#updateAll();
  }

  /** Set color from any valid color string */
  setColor(colorString: string): boolean {
    const parsed = parseColor(colorString);
    if (parsed) {
      this.#hue = parsed.hsv.h;
      this.#saturation = parsed.hsv.s;
      this.#brightness = parsed.hsv.v;
      if (this.hasAttribute('opacity')) {
        this.#alpha = parsed.alpha;
      }
      this.#updateAll();
      return true;
    }
    return false;
  }

  /** Get color in specific format */
  getFormattedValue(format: ColorFormat): string {
    const originalFormat = this.format;
    this.format = format;
    const value = this.#formatValue();
    this.format = originalFormat;
    return value;
  }
}

customElements.define('ry-color-picker', RyColorPicker);
